import { ReactNode, useEffect, useRef, useState } from 'react'
import cx from 'classnames'

import Button from 'common/components/button'
import { CloseSmall } from 'common/icons'

interface Props {
  title: string
  className?: string
  isVisible?: boolean
  children?: ReactNode
  hasMaxWidth?: boolean
  onClose: () => void
}

function AnimatedOverlay({
  title,
  onClose,
  className,
  children,
  isVisible = false,
  hasMaxWidth,
}: Props) {
  const [shouldShow, setShouldShow] = useState(false)
  const isMountedVisible = useRef(isVisible)

  useEffect(() => {
    let clearTimeoutId: NodeJS.Timeout

    // Adding a slight delay when the component is mounted with isVisible=true, to enable the animation
    if (isMountedVisible.current) {
      setTimeout(() => {
        setShouldShow(isVisible)
      }, 100)
    } else {
      setShouldShow(isVisible)
    }

    return () => {
      isMountedVisible.current = false
      clearTimeout(clearTimeoutId)
    }
  }, [isVisible])

  return (
    <div
      data-cy="animated-overlay"
      className={cx(
        'bg-warmGray-50 fixed inset-0 z-110 transition-transform ease-out duration-300 transform',
        {
          'translate-y-0': shouldShow,
          'translate-y-full': !shouldShow,
        },
        className
      )}
    >
      <div className="bg-coolGray-100 border-b-1 border-coolGray-200 h-14">
        <div
          className={cx('flex items-center px-6 py-2', {
            'max-w-363 sm:mx-auto': !hasMaxWidth,
          })}
        >
          <Button onClick={onClose}>
            <CloseSmall />
          </Button>
          <span className="text-lg font-medium text-coolGray-800">{title}</span>
        </div>
      </div>
      <div
        className={cx('w-full h-full', {
          'max-w-363 sm:mx-auto': !hasMaxWidth,
        })}
      >
        {children}
      </div>
    </div>
  )
}

export default AnimatedOverlay
